import { type ElementType, type ReactElement, type Ref } from "react";

import { cx, styled } from "simplydo/react/styled";

import { SIZE_FONT } from "../utils/colors";
import type { MenuProps, MenuSize, MenuVariant } from "./Menu.types";
import { StyledMenuItem } from "./MenuItem";

export type MenuStyleProps = { $size?: MenuSize };

export const StyledMenu = styled.div<MenuStyleProps>`
  display: flex;
  margin: 0 0 1rem 0;
  font-family: inherit;
  font-weight: 400;
  min-height: 2.85714286em;
  background: transparent;
  border: none;
  box-shadow: none;
  flex-direction: row;
  flex-wrap: nowrap;
  width: auto;
  font-size: ${({ $size }) => ($size && SIZE_FONT[$size]) || SIZE_FONT.medium};

  &.is-regular {
    gap: 0.5em;
  }

  &.is-underline {
    border-bottom: 2px solid rgba(34, 36, 38, 0.15);
    border-radius: 0;
  }

  &.is-underline > ${StyledMenuItem}.is-active {
    background: transparent;
    border-bottom: 2px solid #1b1c1d;
    margin-bottom: -2px;
    border-radius: 0;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.95);
  }

  &.is-underline > ${StyledMenuItem}:not(.is-active):hover {
    background: transparent;
    border-bottom: 2px solid #acacac;
    margin-bottom: -2px;
    border-radius: 0;
  }

  &.is-tabular {
    margin: 0;
    border-bottom: 1px solid rgba(34, 36, 38, 0.15);
    border-radius: 0;
  }

  &.is-tabular > ${StyledMenuItem} {
    border-radius: 0.28571429rem 0.28571429rem 0 0;
    border: 1px solid transparent;
    margin-bottom: -1px;
  }

  &.is-tabular > ${StyledMenuItem}.is-active {
    background: #fff;
    border-color: rgba(34, 36, 38, 0.15);
    border-bottom-color: #fff;
    font-weight: 700;
  }

  ${StyledMenuItem}.is-position-right ~ ${StyledMenuItem}.is-position-right {
    margin-left: 0;
  }
`;

const MenuComponent = (
  props: MenuProps & { ref?: Ref<HTMLDivElement> },
): ReactElement => {
  const {
    ref,
    as,
    variant = "regular",
    size,
    className,
    style,
    children,
  } = props;

  const Component = (as || "div") as ElementType;
  const look = variant as MenuVariant;

  return (
    <StyledMenu
      as={Component}
      ref={ref}
      className={cx(className, `is-${look}`)}
      style={style}
      $size={size}
    >
      {children}
    </StyledMenu>
  );
};

export const Menu = styled(MenuComponent)``;
